import type { CurrencyEnum } from '@codezo/graphql';
import { createContext, useContext, type ReactNode } from 'react';
import { useCurrencyStore } from '../../../stores/currency';

/**
 * The handful of things the client quote view cannot decide for itself.
 *
 * Everything else — the queries, the offer card, the accept flow — the view
 * answers on its own. What is left here is what only the surrounding route
 * knows: where an order lives in it, who the viewer is, and whether they are
 * allowed to act at all.
 *
 * Currency is deliberately NOT here — see `useQuoteCurrency`.
 */
export interface QuoteClientViewConfig {
	/**
	 * Open one of the orders this quote produced. Routing is the app's business:
	 * a client goes to `/orders/:id`, a guest to their own shell, and an admin
	 * previewing the page has nowhere to go and passes nothing.
	 */
	onOpenOrder?: (orderId: string, options?: { autoOpenPayment?: boolean }) => void;
	/**
	 * The organization to bill when the session doesn't carry one — a guest has
	 * a valid session but no user object. The quote's own organization wins over
	 * this; it is only the fallback.
	 */
	fallbackOrganizationId?: string;
	/**
	 * The signed-in organization user, offered as the invoice recipient when the
	 * organization has no default on file. A guest session has none, and an
	 * admin previewing the page is not a recipient — both pass nothing.
	 */
	currentOrganizationUser?: {
		id: string;
		firstName?: string | null;
		lastName?: string | null;
	};
	/**
	 * When set, the viewer can read this quote but not act on it, and this is
	 * the reason — shown on the controls it disables.
	 *
	 * The guest shell passes it for an admin's guest preview: a real guest
	 * session against a real client's quote, where the backend refuses every
	 * mutation but a tiny allowlist. Disabling the controls up front is the
	 * courtesy; the refusal is what actually enforces it, and it is
	 * deny-by-default, so this list does not have to be exhaustive to be safe.
	 */
	readOnlyReason?: string;
}

const QuoteClientViewContext = createContext<QuoteClientViewConfig | null>(null);

export function QuoteClientViewProvider({
	children,
	...config
}: QuoteClientViewConfig & { children: ReactNode }) {
	return (
		<QuoteClientViewContext.Provider value={config}>{children}</QuoteClientViewContext.Provider>
	);
}

export function useQuoteClientView(): QuoteClientViewConfig {
	const config = useContext(QuoteClientViewContext);
	if (!config) {
		throw new Error('Quote client view must be rendered inside QuoteClientViewProvider');
	}
	return config;
}

/**
 * The currency every figure on this page is in.
 *
 * Read straight from the store rather than handed in through the config, and
 * that is load-bearing: this value is BOTH the `inCurrency` the server converts
 * amounts with and the symbol `CurrencyText` prints. Two sources could drift by
 * one render and put a euro sign on kroner — silently wrong money on an offer
 * someone accepts. One source makes that unrepresentable.
 *
 * It used to be a prop because this view was a package and a package cannot
 * reach an app's store. It is in the app now.
 */
export function useQuoteCurrency(): CurrencyEnum {
	return useCurrencyStore().selectedCurrency;
}

/**
 * Why this rendering of the quote can't be acted on, or `null` when it can.
 * See `readOnlyReason` — a truthy value both disables a control and explains it.
 */
export function useReadOnlyReason(): string | null {
	return useQuoteClientView().readOnlyReason ?? null;
}
